import { useState, type FormEvent } from 'react';
import type { Team } from '@nepal-football/shared';

import { useCreateTeam, useUpdateTeam } from '../api/hooks';
import { ApiClientError } from '../api/client';

export interface TeamFormProps {
  /** When provided, the form opens in edit mode. */
  team?: Team;
  onSave: (t: Team) => void;
  onCancel: () => void;
}

export default function TeamForm({ team, onSave, onCancel }: TeamFormProps) {
  const isEdit = team !== undefined;
  const createMutation = useCreateTeam();
  const updateMutation = useUpdateTeam();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const [name,         setName]         = useState(team?.name         ?? '');
  const [city,         setCity]         = useState(team?.city         ?? '');
  const [captainName,  setCaptainName]  = useState(team?.captainName  ?? '');
  const [instagramUrl, setInstagramUrl] = useState(team?.instagramUrl ?? '');
  // Members entered as one name per line for simplicity.
  const [membersText,  setMembersText]  = useState(
    (team?.members ?? []).join('\n'),
  );

  // Visual / display fields from the legacy schema — keep editable in edit mode
  // so admin-managed teams render consistently with the seeded ones.
  const [icon,  setIcon]  = useState(team?.icon  ?? '⚽');
  const [color, setColor] = useState(team?.color ?? '#003893');

  const [errors, setErrors] = useState<{ name?: string; insta?: string; submit?: string }>({});

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();

    const validation: typeof errors = {};
    const trimmedName = name.trim();
    if (trimmedName.length === 0) validation.name = 'Team name is required';
    if (
      instagramUrl.trim().length > 0 &&
      !/^https:\/\//i.test(instagramUrl.trim())
    ) {
      validation.insta = 'Instagram URL must start with https://';
    }
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    setErrors({});

    const members = membersText
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const payload = {
      name: trimmedName,
      icon,
      color,
      // Sensible defaults so legacy fields remain valid for new entries.
      count: team?.count ?? 0,
      tasks: team?.tasks ?? [],
      countLabel: team?.countLabel,
      city:         city.trim()         || undefined,
      captainName:  captainName.trim()  || undefined,
      instagramUrl: instagramUrl.trim() || undefined,
      members:      members.length > 0 ? members : undefined,
    };

    try {
      const result = isEdit
        ? await updateMutation.mutateAsync({ id: team!.id, ...payload })
        : await createMutation.mutateAsync(payload);
      onSave(result);
    } catch (err) {
      const message =
        err instanceof ApiClientError ? err.message : 'Could not save team.';
      setErrors({ submit: message });
    }
  }

  return (
    <form className="team-form" onSubmit={handleSubmit} noValidate>
      <div className="modal-header">
        <span className="modal-title">
          {isEdit ? '✏️ Edit Team' : '➕ Add Team'}
        </span>
        <button
          type="button"
          className="modal-close"
          onClick={onCancel}
          aria-label="Close form"
        >
          ✕
        </button>
      </div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="team-name">Team name *</label>
        <input
          id="team-name"
          className="reg-input"
          type="text"
          maxLength={100}
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-invalid={!!errors.name}
          required
        />
        {errors.name && <div className="reg-error" role="alert">{errors.name}</div>}
      </div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="team-city">Represented city</label>
        <input
          id="team-city"
          className="reg-input"
          type="text"
          maxLength={100}
          value={city}
          onChange={(e) => setCity(e.target.value)}
        />
      </div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="team-captain">Captain name</label>
        <input
          id="team-captain"
          className="reg-input"
          type="text"
          maxLength={100}
          value={captainName}
          onChange={(e) => setCaptainName(e.target.value)}
        />
      </div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="team-members">
          Team members (one name per line, up to 30)
        </label>
        <textarea
          id="team-members"
          className="reg-input"
          rows={6}
          value={membersText}
          onChange={(e) => setMembersText(e.target.value)}
          placeholder={'Player one\nPlayer two\n…'}
        />
      </div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="team-insta">Instagram URL</label>
        <input
          id="team-insta"
          className="reg-input"
          type="url"
          inputMode="url"
          maxLength={200}
          placeholder="https://instagram.com/your-team"
          value={instagramUrl}
          onChange={(e) => setInstagramUrl(e.target.value)}
          aria-invalid={!!errors.insta}
        />
        {errors.insta && <div className="reg-error" role="alert">{errors.insta}</div>}
      </div>

      <div className="reg-field reg-field-row">
        <div style={{ flex: '0 0 110px' }}>
          <label className="reg-label" htmlFor="team-icon">Icon</label>
          <input
            id="team-icon"
            className="reg-input"
            type="text"
            maxLength={50}
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
          />
        </div>
        <div style={{ flex: '0 0 130px' }}>
          <label className="reg-label" htmlFor="team-color">Accent color</label>
          <input
            id="team-color"
            className="reg-input"
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            style={{ height: 42, padding: 4 }}
          />
        </div>
      </div>

      {errors.submit && (
        <div className="reg-error" role="alert" style={{ marginBottom: 12 }}>
          ⚠️ {errors.submit}
        </div>
      )}

      <div className="form-actions">
        <button
          type="button"
          className="reg-btn reg-btn-ghost"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="reg-btn"
          disabled={isPending}
          aria-busy={isPending}
        >
          {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add team'}
        </button>
      </div>
    </form>
  );
}
